import { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { useAppEvent } from '../../AppContext.tsx'
import { CollapsibleGroup } from '../Collapsible.tsx'
import { LAYOUTS } from '../../layout/index.ts'
import { FORCE_BOUNDS, defaultSettings } from '../../settings.ts'

/**
 * Layout settings: pick the engine and, for the force simulation, tune how the
 * graph spreads. Repulsion is the headline knob — bump it up to pull a dense
 * diagram apart. The values are part of the persisted, serialisable workbench
 * settings (see {@link AppState.setSettings}).
 *
 * Tucked into a collapsible group (collapsed by default) so these
 * occasionally-tuned knobs don't crowd the main run controls.
 */
export function LayoutSettings() {
  const app = useAppEvent('settings')
  const [collapsed, setCollapsed] = useState(true)
  const s = app.settings
  const force = s.force
  const isForce = s.layout === 'force'

  // Repulsion is shown as a positive magnitude (the d3 charge is negative), so a
  // larger slider value = more spread, which reads naturally.
  const repulsion = -force.charge
  const repMin = -FORCE_BOUNDS.charge.max // 0
  const repMax = -FORCE_BOUNDS.charge.min // 3000

  const def = defaultSettings()
  const atDefaults =
    s.layout === def.layout &&
    force.charge === def.force.charge &&
    force.linkDistance === def.force.linkDistance &&
    force.collideRadius === def.force.collideRadius

  return (
    <section className="panel-section">
      <CollapsibleGroup
        title="Layout"
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        right={
          <button
            className="ghost small"
            title="Reset layout & force settings to defaults"
            disabled={atDefaults}
            onClick={() => app.setSettings({ layout: def.layout, force: def.force })}
          >
            <RotateCcw size={12} /> Reset
          </button>
        }
      >
        <div className="settings-body">
          <label className="field">
            <span className="field-label">Engine</span>
            <select value={s.layout} onChange={(e) => app.setSettings({ layout: e.target.value as typeof s.layout })}>
              {LAYOUTS.map((l) => (
                <option key={l.kind} value={l.kind}>
                  {l.label} — {l.blurb}
                </option>
              ))}
            </select>
          </label>

          {/* Force knobs only bite when the force engine is active; show them disabled
              otherwise so the panel doesn't jump around when switching engines. */}
          <fieldset
            className="force-knobs"
            disabled={!isForce}
            title={isForce ? undefined : 'Force settings apply to the Force layout'}
          >
            <label className="field">
              <span className="field-label">
                Repulsion <span className="field-value">{repulsion}</span>
              </span>
              <input
                type="range"
                min={repMin}
                max={repMax}
                step={10}
                value={repulsion}
                title="How hard nodes push each other apart. Raise it to spread out a dense graph."
                onChange={(e) => app.setSettings({ force: { ...force, charge: -Number(e.target.value) } })}
              />
            </label>

            <label className="field">
              <span className="field-label">
                Link distance <span className="field-value">{force.linkDistance}</span>
              </span>
              <input
                type="range"
                min={FORCE_BOUNDS.linkDistance.min}
                max={FORCE_BOUNDS.linkDistance.max}
                step={5}
                value={force.linkDistance}
                title="Target spring length between connected nodes."
                onChange={(e) => app.setSettings({ force: { ...force, linkDistance: Number(e.target.value) } })}
              />
            </label>

            <label className="field">
              <span className="field-label">
                Collision radius <span className="field-value">{force.collideRadius}</span>
              </span>
              <input
                type="range"
                min={FORCE_BOUNDS.collideRadius.min}
                max={FORCE_BOUNDS.collideRadius.max}
                step={1}
                value={force.collideRadius}
                title="Keep node bodies at least this far apart (0 = off)."
                onChange={(e) => app.setSettings({ force: { ...force, collideRadius: Number(e.target.value) } })}
              />
            </label>
          </fieldset>
        </div>
      </CollapsibleGroup>
    </section>
  )
}
